import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Card,
  CheckCircleIcon,
  ClockIcon,
  Cluster,
  DocumentIcon,
  Grid,
  LayersIcon,
  Page,
  QueryState,
  SegmentBar,
  ShieldIcon,
  SparkleIcon,
  Stack,
  StatStrip,
  TargetIcon,
  type Tone,
} from '@integra/ui';
import type {
  ChannelSpec,
  ChannelType,
  SpecHealth,
  SpecHealthReport,
  SpecHealthStatus,
} from '../api/types.js';
import { useChannelSpecs, useChannelSpecHealth } from '../api/hooks.js';
import { BrandLogo, CHANNEL_LOGO } from './brandLogos.js';

const TYPE_TONE: Record<ChannelType, Tone> = {
  social: 'info',
  commerce: 'success',
  messaging: 'warning',
  cms: 'neutral',
  ingest: 'neutral',
};

const HEALTH_TONE: Record<SpecHealthStatus, Tone> = {
  ok: 'success',
  stale: 'warning',
  drift: 'error',
};

const HEALTH_LABEL: Record<SpecHealthStatus, string> = {
  ok: 'In sync',
  stale: 'Due re-review',
  drift: 'Drifted',
};

/**
 * Spec drift health (§9 Phase 5): a standing check that every pinned spec is still
 * fresh and still matches its connector's own format contract. Best practices are
 * human-reviewed, not machine-verified, and this says so rather than implying a
 * live green check (§2.7).
 */
function SpecHealthWidget({ report }: { report: SpecHealthReport }) {
  const issues = report.channels.filter((c) => c.status !== 'ok');
  return (
    <Card
      title="Spec health at a glance"
      subtitle={`Checked ${String(report.total)} channels · re-review SLA ${String(report.slaDays)} days`}
    >
      <Stack gap="md">
        <SegmentBar
          segments={[
            { label: 'In sync', value: report.ok, series: 'success' },
            { label: 'Due review', value: report.stale, series: 'warning' },
            { label: 'Drifted', value: report.drift, series: 'error' },
          ]}
        />
        {issues.length === 0 ? (
          <Cluster gap="sm" align="center">
            <Badge tone="success" dot>
              All in sync
            </Badge>
            <span className="text-xs text-ink-muted">
              Every spec matches its connector contract and is within the re-review window.
            </span>
          </Cluster>
        ) : (
          <ul className="space-y-1.5 text-xs">
            {issues.map((c) => (
              <li key={c.connectorKey}>
                <Cluster gap="sm">
                  <Badge tone={HEALTH_TONE[c.status]} dot>
                    {HEALTH_LABEL[c.status]}
                  </Badge>
                  <span className="font-medium text-navy-700">{c.displayName}</span>
                  <span className="text-ink-muted">{healthReason(c)}</span>
                </Cluster>
              </li>
            ))}
          </ul>
        )}
        <p className="text-[10px] text-ink-muted">
          Format limits auto-verified per connector · best practices human-reviewed.
        </p>
      </Stack>
    </Card>
  );
}

function healthReason(c: SpecHealth): string {
  if (c.status === 'drift') return c.consistency.differences.join('; ');
  if (c.status === 'stale') {
    return `last verified ${c.freshness.lastVerified} (${String(c.freshness.ageDays)} days ago)`;
  }
  return '';
}

function mib(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 1024 * 1024 * 100 ? 0 : 1)} MB`;
}

function titleCaseKey(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/** A small-caps section eyebrow — one consistent label style across the card. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-ink-muted">
      {children}
    </p>
  );
}

/** A compact labelled fact chip — scannable at a glance, not a run-on line. */
function Chip({ label, value }: { label: string; value: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-edge bg-surface-sunken px-2.5 py-1 text-xs">
      <span className="text-[11px] uppercase tracking-wide text-ink-muted">{label}</span>
      <span className="font-medium text-navy-700">{value}</span>
    </span>
  );
}

/**
 * A channel's real brand mark in a branded tile — the consistent iconography unit
 * for a channel. Falls back to a neutral layers glyph for the few connectors
 * without a mark, so the tile is never empty.
 */
function ChannelLogoTile({
  connectorKey,
  size = 'md',
}: {
  connectorKey: string;
  size?: 'sm' | 'md';
}) {
  const logoId = CHANNEL_LOGO[connectorKey];
  const box = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
  const glyph = size === 'sm' ? 15 : 18;
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-lg bg-navy-800 text-gold-400 ${box}`}
    >
      {logoId !== undefined ? <BrandLogo id={logoId} size={glyph} /> : <LayersIcon size={glyph} />}
    </span>
  );
}

/**
 * The capability, shown not told: one source asset fanning out to channel-correct
 * renditions, each in its native frame with the format it will be fitted to. This
 * is what "post once, land correctly everywhere" looks like.
 */
const FANOUT_FRAMES = [
  { connectorKey: 'twitter', frame: 'h-8 w-12', ratio: '16:9' },
  { connectorKey: 'meta-graph', frame: 'h-11 w-9', ratio: '4:5' },
  { connectorKey: 'tiktok-content', frame: 'h-12 w-7', ratio: '9:16' },
  { connectorKey: 'pinterest', frame: 'h-12 w-8', ratio: '2:3' },
  { connectorKey: 'linkedin', frame: 'h-8 w-12', ratio: '1.91:1' },
] as const;

function ChannelFanout() {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-edge bg-gradient-to-b from-surface-muted to-surface p-5">
      <div className="flex w-full max-w-[16rem] items-center gap-2.5 rounded-lg border border-edge bg-surface p-2.5 shadow-card">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold-200 text-gold-600">
          <SparkleIcon size={16} />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-navy-700">One source asset</p>
          <p className="text-[11px] text-ink-muted">Written and approved once</p>
        </div>
      </div>
      <span aria-hidden="true" className="h-4 w-px bg-edge-strong" />
      <p className="text-[10px] font-semibold uppercase tracking-widest text-gold-text">
        Fitted to every channel
      </p>
      <div className="flex flex-wrap items-end justify-center gap-3">
        {FANOUT_FRAMES.map((f) => (
          <div key={f.connectorKey} className="flex flex-col items-center gap-1.5">
            <div
              className={`flex items-center justify-center rounded-md border border-edge bg-surface-sunken text-navy-600 ${f.frame}`}
            >
              {CHANNEL_LOGO[f.connectorKey] !== undefined && (
                <BrandLogo id={CHANNEL_LOGO[f.connectorKey] ?? ''} size={15} />
              )}
            </div>
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-state-success">
              <CheckCircleIcon size={10} /> {f.ratio}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelCard({ spec }: { spec: ChannelSpec }) {
  const f = spec.format;
  const bp = spec.bestPractices;
  const hasVideo = f.acceptedVideoTypes !== undefined && f.acceptedVideoTypes.length > 0;
  const sweetSpot = bp.recommendedDurationSeconds?.sweetSpot;
  // Whether the "at a glance" facts row has anything to show — keeps an empty
  // grid from leaving a gap above the guidance bullets.
  const hasFacts =
    bp.recommendedAspectRatios !== undefined ||
    sweetSpot !== undefined ||
    bp.tone !== undefined ||
    bp.hashtags !== undefined;

  return (
    <Card>
      <Stack gap="md">
        {/* Branded header — the channel's own mark, name, and one-line standard. */}
        <Cluster justify="between" align="start" gap="md">
          <Cluster gap="sm" align="center">
            <ChannelLogoTile connectorKey={spec.connectorKey} />
            <div className="min-w-0">
              <p className="font-heading text-sm text-navy-700">{spec.displayName}</p>
              <p className="text-xs leading-snug text-ink-muted">{bp.summary}</p>
            </div>
          </Cluster>
          <Badge tone={TYPE_TONE[spec.channelType]}>{spec.channelType}</Badge>
        </Cluster>

        {/* Hard limits — what the publish gate enforces, as scannable chips. */}
        <section>
          <SectionLabel>Enforced on publish</SectionLabel>
          <Cluster gap="sm">
            <Chip label="Images" value={f.acceptedFileTypes.join(', ')} />
            <Chip label="Max file" value={mib(f.maxFileSizeBytes)} />
            {Object.entries(f.characterLimits).map(([k, v]) => (
              <Chip key={k} label={titleCaseKey(k)} value={`≤ ${v.toLocaleString()}`} />
            ))}
            {hasVideo && (
              <Chip
                label="Video"
                value={`${f.acceptedVideoTypes?.join(', ') ?? ''}${
                  f.minDurationSeconds !== undefined && f.maxDurationSeconds !== undefined
                    ? ` · ${String(f.minDurationSeconds)}–${String(f.maxDurationSeconds)}s`
                    : ''
                }`}
              />
            )}
          </Cluster>
        </section>

        {/* Best practice — the editorial gold standard, chips then short guidance. */}
        {(hasFacts || (bp.notes !== undefined && bp.notes.length > 0)) && (
          <section>
            <SectionLabel>Best practice</SectionLabel>
            {hasFacts && (
              <Cluster gap="sm" className="mb-2">
                {bp.recommendedAspectRatios !== undefined && (
                  <Chip label="Aspect" value={bp.recommendedAspectRatios.join(', ')} />
                )}
                {sweetSpot !== undefined && (
                  <Chip label="Sweet spot" value={`~${String(sweetSpot)}s`} />
                )}
                {bp.tone !== undefined && <Chip label="Tone" value={bp.tone} />}
                {bp.hashtags !== undefined && <Chip label="Hashtags" value={bp.hashtags} />}
              </Cluster>
            )}
            {bp.notes !== undefined && bp.notes.length > 0 && (
              <ul className="space-y-1 text-xs text-ink-muted">
                {bp.notes.map((note, i) => (
                  <li key={i} className="flex gap-2">
                    <span aria-hidden="true" className="mt-px shrink-0 text-gold-500">
                      •
                    </span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Provenance — sourced and dated, as compact chips not a sentence. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-edge pt-3 text-[10px] text-ink-muted">
          <a
            href={spec.provenance.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-gold-text hover:underline"
          >
            Official docs ↗
          </a>
          <span>Verified {spec.provenance.lastVerified}</span>
          <span>Spec v{spec.provenance.specVersion}</span>
        </div>
      </Stack>
    </Card>
  );
}

/**
 * Channel standards (§6.4, §9): the gold standard per channel — the format limits
 * INTEGRA enforces and the best practices it transforms content toward, each value
 * sourced from the channel's official docs and dated. This is the reference behind
 * "post once, land correctly everywhere, never mis-mapped".
 */
export function ChannelStandardsPage() {
  const { workspaceId = '' } = useParams();
  const specs = useChannelSpecs(workspaceId);
  const health = useChannelSpecHealth(workspaceId);
  const specList = specs.data?.data ?? [];
  const report = health.data;

  return (
    <Page
      title="Channel standards"
      icon={LayersIcon}
      description="Sourced, versioned format rules for every channel — enforced on publish, and the gold standard content is transformed toward."
      stats={
        report !== undefined ? (
          <StatStrip
            stats={[
              { icon: LayersIcon, label: 'Channels', value: report.total, tone: 'accent' },
              { icon: CheckCircleIcon, label: 'In sync', value: report.ok },
              { icon: ClockIcon, label: 'Due review', value: report.stale },
              { icon: TargetIcon, label: 'Drifted', value: report.drift },
            ]}
          />
        ) : specList.length > 0 ? (
          <StatStrip
            stats={[
              { icon: LayersIcon, label: 'Channels', value: specList.length, tone: 'accent' },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        {/* Hero — the capability shown, not told: copy left, live fan-out right. */}
        <Card>
          <div className="grid items-center gap-6 md:grid-cols-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gold-text">
                Channel Spec Library
              </p>
              <h2 className="mt-2 font-heading text-2xl leading-tight text-navy-800">
                Post once. Land correctly everywhere.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                Every channel&rsquo;s hard format limits are enforced at the publish gate, and its
                best practices guide how content is transformed — each value sourced from the
                official docs, dated, and versioned.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <Cluster gap="sm" align="center">
                  <span className="text-state-success">
                    <ShieldIcon size={15} />
                  </span>
                  <span className="text-xs text-navy-700">
                    Hard limits blocked at the publish gate
                  </span>
                </Cluster>
                <Cluster gap="sm" align="center">
                  <span className="text-gold-600">
                    <SparkleIcon size={15} />
                  </span>
                  <span className="text-xs text-navy-700">
                    Content fitted to each channel&rsquo;s gold standard
                  </span>
                </Cluster>
                <Cluster gap="sm" align="center">
                  <span className="text-navy-500">
                    <DocumentIcon size={15} />
                  </span>
                  <span className="text-xs text-navy-700">
                    Every value sourced, dated, and versioned
                  </span>
                </Cluster>
              </div>
            </div>
            <ChannelFanout />
          </div>
        </Card>

        <QueryState loading={health.isLoading} error={health.error} what="spec drift health">
          {report !== undefined && <SpecHealthWidget report={report} />}
        </QueryState>

        <QueryState loading={specs.isLoading} error={specs.error} what="the channel standards">
          <Grid min="lg" gap="md">
            {specList.map((spec) => (
              <ChannelCard key={spec.connectorKey} spec={spec} />
            ))}
          </Grid>
        </QueryState>
      </Stack>
    </Page>
  );
}
